import { crudActions } from "@/utils/store";
import isEmpty from "lodash/isEmpty.js"
import { PublicObject } from "@/utils/dto/objects.js";

const BASE_URL = '/admin/objects/'
const BASE_PUBLIC_URL = `${process.env.serverPath}/public/objects`

export const state = () => ({
  objects: [],
  withMapObjects: [],
  total: 0,
  cachedObjects: [],
  typesAndTemplates: {},
  mainEvents: [],
  mainObjects: [],
  mainObjectsTotal: 0,
  mainFacts: [],
  mainQuotes: [],
  footerStats: {},
  isSpecialProject: false,
})

export const mutations = {
  SET_OBJECTS(state, payload) {
    state.objects = payload.objects.map(x => {
      x.key = x.id
      return x
    });
    state.total = payload.total;
  },
  SET_WITH_MAP_OBJECTS(state, payload) {
    state.withMapObjects = payload.objects.map(x => {
      x.key = x.id
      return x
    });
    state.total = payload.total;
  },
  UPDATE_OBJECT(state, payload) {
    state.objects = [...state.objects.map(x => {
      if (x.id === payload.id) {
        return payload
      }
      return x
    })];
  },
  PUSH_TO_CACHE_OBJ(state, payload) {
    state.cachedObjects.push(...payload)
  },
  RESET_OBJECTS_CACHE(state) {
    state.cachedObjects = []
  },
  SAVE_TYPES_AND_TEMPLATES(state, payload) {
    state.typesAndTemplates = payload
  },
  SET_MAIN_EVENTS(state, payload) {
    state.mainEvents = payload
  },
  SET_MAIN_OBJECTS(state, payload) {
    state.mainObjects = payload
  },
  SET_MAIN_OBJECTS_TOTAL(state, total) {
    state.mainObjectsTotal = total
  },
  SET_MAIN_FACTS(state, payload) {
    state.mainFacts = payload
  },
  SET_MAIN_QUOTES(state, payload) {
    state.mainQuotes = payload
  },
  SET_FOOTER_STATS(state, payload) {
    state.footerStats = payload
  },
  SET_SPECIAL_PROJECT(state, payload) {
    state.isSpecialProject = payload
  },
}

export const actions = {
  ...crudActions(BASE_URL),
  async fetchObjects({ commit }, params = {}) {
    const response = await this.$axios.$get(BASE_URL, {
      params: {
        ...params
      }
    })
    response.items = (response.items || []).map(x => new PublicObject(x))
    commit('SET_OBJECTS', { objects: response['items'], total: response['total'] })
    return response
  },
  async fetchPublicObjects({ commit }, params = {}) {
    const response = await this.$axios.$get(`${BASE_PUBLIC_URL}/`, {
      params: {
        ...params
      }
    })
    response.items = (response.items || []).map(x => new PublicObject(x))
    commit('SET_OBJECTS', { objects: response['items'], total: response['total'] })
    return response
  },
  async fetchWithMapObjects({ commit }, params = {}) {
    const response = await this.$axios.$get(`${BASE_URL}custom_schema_list/with_map/`, {
      params: {
        with_map: true,
        ...params
      }
    })
    response.items = (response.items || []).map(x => new PublicObject(x))
    commit('SET_WITH_MAP_OBJECTS', { objects: response['items'], total: response['total'] })
    return response
  },
  async fetchMyObjects({ commit }, params = {}) {
    const response = await this.$axios.$get(`${BASE_URL}my/`, {
      params: {
        ...params
      }
    })
    response.items = (response.items || []).map(x => new PublicObject(x))
    return response
  },
  async fetchAdminList({ commit }, params = {}) {
    const response = await this.$axios.$get(`${BASE_URL}admin_list/`, {
      params: {
        ...params
      }
    })
    response.items = (response.items || []).map(x => new PublicObject(x))
    return response
  },
  async createWorkArea({ dispatch }, { vistat_object, data }) {
    const payload = { ...data }
    delete payload.image

    const response = await this.$axios.$post('/admin/object_images/', {
      vistat_object: vistat_object,
      polygons: payload,
      label: payload.label
    })

    return dispatch('workAreaUploadImage', { id: response.id, file: data.image })
  },
  async updateWorkArea({}, { id, vistat_object, data }) {
    const payload = { ...data }
    delete payload.polygons.image

    return this.$axios.$post(`/admin/object_images/${id}/`, {
      vistat_object: vistat_object,
      polygons: payload,
      label: payload.label
    })
  },
  async deleteWorkArea({}, id) {
    return this.$axios.$delete(`/admin/object_images/${id}/`)
  },
  async workAreaUploadImage({}, { id, file }) {
    const formData = new FormData();
    const base64Response = await fetch(file);
    const blob = await base64Response.blob();
    formData.append('file', blob);

    return this.$axios.$post(`/admin/object_images_files/${id}/?content_type=${encodeURIComponent(blob.type)}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },
  async deleteMany(state, object_ids) {
    return this.$axios.$delete(`/admin/objects/many/${object_ids.join(',')}/`)
  },
  async reorderImages(state, payload) {
    return this.$axios.$post(`/admin/object_images/reorder/`, {
      data: payload
    })
  },
  async hideObject({}, id) {
    return this.$axios.$post(`${BASE_URL}hide/${id}/`)
  },
  async unhideObject({}, id) {
    return this.$axios.$post(`${BASE_URL}unhide/${id}/`)
  },
  publicObjects({}, params = {}) {
    return this.$axios.$get(`${BASE_PUBLIC_URL}/`, {
      params: { is_about_project: false, ...params }
    })
      .then(response => {
        return {
          ...response,
          items: response.items.map(item => new PublicObject(item))
        }
      })

  },
  async publicObjectsByAuthor({}, params = {}) {
    const { user, user_id, ...rest } = params
    const authorId = user ?? user_id
    const query = {
      is_about_project: false,
      is_published: 1,
      with_events: true,
      ...(authorId ? { user: authorId } : {}),
      ...rest,
    }
    const v2Base = process.env.BASE_URL_V2
    const urls = v2Base
      ? [`${v2Base}/public/objects/`, `${BASE_PUBLIC_URL}/`]
      : [`${BASE_PUBLIC_URL}/`]

    for (const url of urls) {
      try {
        const response = await this.$axios.$get(url, { params: query })
        if (Array.isArray(response?.items)) {
          return {
            ...response,
            items: response.items.map((item) => new PublicObject(item)),
          }
        }
      } catch (e) {
        // try next base URL
      }
    }
    return { items: [], total: 0 }
  },
  publicObject({}, id) {
    return this.$axios.$get(`${BASE_PUBLIC_URL}/${id}/`)
  },
  randomObject({}, params = {}) {
    return this.$axios.$get(`${BASE_PUBLIC_URL}/random/`, {
      params: { ...params }
    })
  },
  async publicSearch({ dispatch, rootState }, key) {
    let params = { limit: 10 }
    if (typeof key === "string") {
      params.q = key
    } else {
      Object.assign(params, key)
    }
    const results = []

    await dispatch('publicObjects', params)
      .then(response => {
        results.push(...response.items.map(x => {
          x.fuzzy = x.search_group !== 'label'
          if (rootState.auth.loggedIn && ['A', 'CA'].includes(rootState.auth.user.role)) {
            x.label = x.score ? `${x.label} (${x.score})` : x.label
          }
          return x
        }))
      })

    return results
  },
  async getTypesAndTemplatesConsts({ state, commit }) {
    if (isEmpty(state.typesAndTemplates)) {
      commit('SAVE_TYPES_AND_TEMPLATES', await this.$axios.$get(`/constants/types_and_templates/`))
    }

    return state.typesAndTemplates
  },
  async editAdminObject({}, payload) {
    return this.$axios.$post('/admin/object_edit/', payload)
  },
  async getAdminObject({}, id) {
    return this.$axios.$get(`/admin/object_edit/${id}/`)
  },
  addToCache({ state, commit }, obj) {
    if (Array.isArray(obj)) {
      const candidateToAdd = []

      obj.forEach(item => {
        if (!state.cachedObjects.find(x => x.key === item.id)) {
          candidateToAdd.push(item)
        }
      })
      if (candidateToAdd.length) {
        commit('PUSH_TO_CACHE_OBJ', candidateToAdd)
      }
    } else {
      if (!state.cachedObjects.find(x => x.key === obj.id)) {
        commit('PUSH_TO_CACHE_OBJ', [obj])
      }
    }
  },
  resetCacheObjects({ commit }) {
    commit('RESET_OBJECTS_CACHE')
  },
  fetchAndSaveCache({ state, commit, dispatch }, ids) {
    const needToFetch = ids.filter(x => !state.cachedObjects.find(obj => obj.key === x))

    if (!needToFetch.length) {
      return Promise.resolve()
    }

    return dispatch('publicSearch', {
      ids: needToFetch.join(','),
      limit: needToFetch.length
    })
      .then(items => {
        dispatch('addToCache', items.map(x => {
          return {
            label: x.label,
            key: x.id,
            fuzzy: x.fuzzy,
            raw: x
          }
        }))
      })
  },
  registerView({}, slug) {
    return this.$axios.$post(`${process.env.BASE_URL_V2}/public/objects/register_view/${slug}/`, {
      referer: document.referrer
    })
  },
  fetchAttrsOfManyObjects({}, ids) {
    return this.$axios.$get(`${BASE_PUBLIC_URL}/attrs/?ids=${ids.map(x => `${x[0]}.${x[1]}`).join(',')}`)
  },
  checkCanEdit({}, id) {
    return this.$axios.$get(`${BASE_PUBLIC_URL}/${id}/acl/`)
  },
  fetchSingleInfographic({}, id) {
    return this.$axios.$get(`${process.env.BASE_URL_V2}/admin/infographic_edit/${id}/`)
  },
  fetchObjectsInfographic({}, vistat_id) {
    return this.$axios.$get(`${process.env.BASE_URL_V2}/admin/infographics/?vistat_object=${vistat_id}`)
      .then(r => r.items)
  },
  fetchRelatedObjects(context, { slug, params }) {
    return this.$axios.$get(`${process.env.BASE_URL_V2}/public/objects_graph/from_distinct/${slug}/`, { params })
      .then(data => data.items)
  },
  setSpecialProject({ commit }) {
    commit('SET_SPECIAL_PROJECT', true);
  },
  async fetchFooterStats({ state, commit }) {
    if (isEmpty(state.footerStats)) {
      const response = await this.$axios.$get(`${process.env.BASE_URL_V2}/public/footer/stats/`)

      commit('SET_FOOTER_STATS', response)
    }
  },
  async getMainEvents({ state, commit }) {
    if (isEmpty(state.mainEvents)) {
      const objects = await this.$axios.$get(`/public/objects/valid_events/`, {
        params: { limit: 12 },
      })

      commit('SET_MAIN_EVENTS', objects.items)
    }

    return state.mainEvents
  },
  async getMainObjects({ state, commit, dispatch }, params) {
    const objects = await dispatch('publicObjects', {
      ...params,
      q: params?.q ?? '',
      limit: params?.limit ?? 12,
      offset: params?.offset ?? 0,
      // with_events: true,
      order: params?.order ?? '-total_score',
    })

    if (typeof objects.total === 'number') {
      commit('SET_MAIN_OBJECTS_TOTAL', objects.total)
    }

    if (params?.offset && params?.offset > 0) {
      commit('SET_MAIN_OBJECTS', [...state.mainObjects, ...objects.items])
    } else {
      commit('SET_MAIN_OBJECTS', objects.items)
    }

    return objects
  },
  async getMainFacts({ state, commit }) {
    if (isEmpty(state.mainFacts)) {
      const objects = await this.$axios.$get(`/public/object_facts/random/`)

      commit('SET_MAIN_FACTS', objects)
    }

    return state.mainFacts
  },
  async getMainQuotes({ state, commit }) {
    if (isEmpty(state.mainQuotes)) {
      const objects = await this.$axios.$get(`/public/object_quotes/random/`)

      commit('SET_MAIN_QUOTES', objects)
    }

    return state.mainQuotes
  },
}

export const getters = {
  getObjects: (state) => {
    return state.objects
  },
  getWithMapObjects: (state) => {
    return state.getWithMapObjects
  },
  cachedObjectsGetter: (state) => state.cachedObjects,
  getFooterStats: (state) => state.footerStats,
  getMainEvents: (state) => state.mainEvents,
  getMainObjects: (state) => state.mainObjects,
  getMainObjectsTotal: (state) => state.mainObjectsTotal,
  getMainFacts: (state) => state.mainFacts,
  getMainQuotes: (state) => state.mainQuotes,
  getSpecialProject: (state) => state.isSpecialProject,
}
